import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { InlineNotification, Modal } from '@carbon/react';
import {
  EntityForm,
  EntityFormHandle,
  type EntitySeedValues,
  useEntityFormServerErrors,
} from 'components/entity-form';
import { nameKey, type ConsolidationEntityType } from './consolidation-utils';
import {
  entityCanonizeAtom,
  entityCanonizeUrl,
  type EntityCanonizeResponse,
} from './atoms';

// An entity already present in the reconciliation, used to classify a name
// collision (§3.9 / #25) by what the matching twin actually is.
export interface CanonizeExistingEntity {
  id: number;
  name: string;
  type: ConsolidationEntityType;
}

interface Props {
  open: boolean;
  // The Imported entity being converted (its wrapper Entity id + name).
  entityId: number;
  name: string;
  sessionId: string;
  // When the reconciliation has already established a canonical type, default the
  // new entity to that type (§3.9 / #24). Both options remain available otherwise.
  establishedType: ConsolidationEntityType | null;
  // §3.10 / #24: true when the Imported entity is already included in the
  // reconciliation (its checkbox is on). Combined with an established canonical
  // type this FIXES the new entity's type — it must match the survivor, because
  // an already-included entity can't switch the reconciliation's single type
  // out from under the structure that depends on it. When it is NOT yet
  // included the type stays editable and a conflicting choice only WARNS.
  subjectInReconciliation?: boolean;
  // Entities already present in the reconciliation (excluding this subject).
  // Drives the #25 name-collision guard: a new canonical whose name matches one
  // of these merges into it rather than standing alone, so the panel warns (or,
  // for an opposite-type canonical twin, blocks) with a type-aware message.
  existingEntities?: CanonizeExistingEntity[];
  onClose: () => void;
  onCanonized: (response: EntityCanonizeResponse) => void;
}

const DESCRIPTION =
  'Add as New creates a real canonical entity — an Individual or Organization — ' +
  'from this Imported entity. It commits immediately as its own action, ' +
  'independent of the surrounding reconciliation, and the new entity joins the ' +
  'reconciliation as its own surviving entity.';

const TYPE_NOUN: Record<ConsolidationEntityType, string> = {
  IndividualEntity: 'Individual',
  OrganizationEntity: 'Organization',
  ImportedEntity: 'Imported',
};

// A submitted payload's chosen canonical type (undefined for an Imported form,
// which this panel never produces).
type ChosenType = 'IndividualEntity' | 'OrganizationEntity' | undefined;

const isCanonical = (t: ConsolidationEntityType) => t !== 'ImportedEntity';

// Reconstruct the entity's display name from the assembled form payload, so the
// name-collision guard can compare it against the reconciliation's names. Mirror
// EntityForm's per-type attribute shape (Individual: first/middle/last;
// Organization: primaryName).
const nameFromPayload = (
  payload: Record<string, unknown>,
  chosen: ChosenType
): string | null => {
  const attrs = payload.concreteEntityAttributes as
    Record<string, unknown> | undefined;
  if (!attrs) return null;
  if (chosen === 'OrganizationEntity') {
    const primary = attrs.primaryName;
    return typeof primary === 'string' && primary.trim() ? primary : null;
  }
  const parts = [attrs.firstName, attrs.middleName, attrs.lastName]
    .filter((p): p is string => typeof p === 'string' && p.trim() !== '')
    .map((p) => p.trim());
  return parts.length ? parts.join(' ') : null;
};

// "Add as New" (Canonize) for the entity-page reconcile screen (§3.9 / §3.10).
// Reuses the shared EntityForm; the imported entity's cited addresses re-point to
// the new canonical during the merge, so only the name is seeded here. Presented
// as a Modal (the Match Review SidePanel is coupled to the doc-structuring shell).
//
// Type constraint (#24): the form defaults to the established canonical type. If
// the subject is already in the reconciliation the type is FIXED — a submit that
// chose the opposite type is refused with an explanation. If the subject is not
// yet included the type is editable, and a conflicting choice WARNS (and merges
// as its own entity rather than the reconciliation's survivor) before it commits.
//
// Name collision (#25): if the new canonical's name matches an entity already in
// the reconciliation, the panel messages the exact outcome by twin type — an
// Imported twin merges into the new entity ("Create and merge"); a same-type
// canonical twin (lower id) survives and the new entity is retired into it
// ("Create anyway"); an opposite-type canonical twin can never merge, so it is
// blocked until the name is changed.
const EntityCanonizePanel: React.FC<Props> = ({
  open,
  entityId,
  name,
  sessionId,
  establishedType,
  subjectInReconciliation = false,
  existingEntities = [],
  onClose,
  onCanonized,
}) => {
  const formRef = useRef<EntityFormHandle>(null);
  const [formValid, setFormValid] = useState(false);
  const {
    error,
    setError,
    fieldErrors,
    clearFieldErrors,
    clearErrors,
    showServerError,
  } = useEntityFormServerErrors();
  // A soft warning the user must acknowledge (submit again) before it commits.
  // We store the message that was last shown so a second identical submit — the
  // acknowledgement — proceeds, while an edit that changes the warning re-shows it.
  const [warning, setWarning] = useState<string | null>(null);
  // The active #25 collision case, so the primary button reads the outcome the
  // acknowledging click will perform (Create and merge / Create anyway).
  const [collisionCase, setCollisionCase] = useState<1 | 2 | null>(null);
  const [canonizeState, triggerCanonize] = useAtom(entityCanonizeAtom);
  const pending = canonizeState.loading;

  // A reopen drops the last attempt's errors.
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    clearErrors();
  }

  // The type is pinned only when the subject is already in the reconciliation AND
  // a canonical type is established (mock AddAsNewEntityPanel.tsx typeFixed).
  const typeFixed = subjectInReconciliation && establishedType !== null;
  const establishedNoun = establishedType ? TYPE_NOUN[establishedType] : null;

  // Twins keyed by normalized name, so a collision can classify by the matching
  // entity's type/id.
  const twinsByNameKey = useMemo(() => {
    const map = new Map<string, CanonizeExistingEntity[]>();
    existingEntities.forEach((e) => {
      const key = nameKey(e.name);
      const bucket = map.get(key);
      if (bucket) bucket.push(e);
      else map.set(key, [e]);
    });
    return map;
  }, [existingEntities]);

  const seedValues: EntitySeedValues = useMemo(
    () => ({
      name: name || null,
      notes: name ? `Original name before parsing: ${name}` : null,
      addresses: [],
      phoneNumbers: [],
      emailAddresses: [],
    }),
    [name]
  );

  const commit = useCallback(
    (payload: Record<string, unknown>) => {
      setWarning(null);
      setCollisionCase(null);
      triggerCanonize({
        url: entityCanonizeUrl(entityId),
        type: 'POST',
        data: { entity: payload, session_id: sessionId },
        onSuccess: (data) => onCanonized(data as EntityCanonizeResponse),
        onError: (err) => showServerError(err, 'Could not add entity'),
      });
    },
    [entityId, sessionId, triggerCanonize, onCanonized, showServerError]
  );

  const handleSubmit = useCallback(
    (payload: Record<string, unknown>) => {
      clearErrors();
      const chosen = payload.concreteEntityType as ChosenType;

      // Hard constraint (#24): a subject already in the reconciliation is fixed
      // to the established canonical type. Refuse a mismatched submit outright.
      if (typeFixed && chosen && chosen !== establishedType) {
        setWarning(null);
        setCollisionCase(null);
        setError(
          `This entity is already in the reconciliation, so it must be added as ${establishedNoun}. ` +
            'Remove it from the reconciliation first to add it as a different type.'
        );
        return;
      }

      const chosenName = nameFromPayload(payload, chosen);
      const newNoun = chosen ? TYPE_NOUN[chosen] : 'entity';
      const subjectRef = `${name} · Imported · #${entityId}`;
      const twins = chosenName
        ? (twinsByNameKey.get(nameKey(chosenName)) ?? [])
        : [];

      // #25 name collision, classified by the strongest constraint the twins
      // impose: an opposite-type canonical blocks; else a same-type canonical
      // (Case 2); else an Imported twin (Case 1).
      const oppositeCanonical = twins.find(
        (t) => isCanonical(t.type) && chosen && t.type !== chosen
      );
      const sameCanonical = twins.find(
        (t) => isCanonical(t.type) && t.type === chosen
      );
      const importedTwin = twins.find((t) => t.type === 'ImportedEntity');

      // Case 3 — opposite-type canonical twin. Identically named records always
      // merge, and an Individual can never merge with an Organization: block.
      if (oppositeCanonical) {
        setWarning(null);
        setCollisionCase(null);
        setError(
          `This name can't be used. You're converting ${subjectRef} into a new ${newNoun} ` +
            `entity named "${chosenName}". "${chosenName}" is already in this reconciliation as an ` +
            `${TYPE_NOUN[oppositeCanonical.type]} · #${
              oppositeCanonical.id
            }. Identically named ` +
            'records always merge, and an Individual can never merge with an Organization. ' +
            `Choose a different name, or remove #${oppositeCanonical.id} from the reconciliation first.`
        );
        return;
      }

      let nextWarning: string | null = null;
      let nextCase: 1 | 2 | null = null;

      if (sameCanonical) {
        // Case 2 — same-type canonical twin. It has the lower id, so it survives
        // and the new record is retired into it on submit (net no-op, an id spent).
        nextWarning =
          `"${chosenName}" is already in this reconciliation. You're converting ${subjectRef} into a new ` +
          `${newNoun} entity named "${chosenName}". Identically named records always merge. ` +
          `${sameCanonical.name} · ${TYPE_NOUN[sameCanonical.type]} · #${
            sameCanonical.id
          } has the lower ` +
          'entity ID, so it will survive and your new entity will be retired into it on submit. ' +
          `To reach the same result without creating a new record, add #${sameCanonical.id} to the reconciliation directly.`;
        nextCase = 2;
      } else if (importedTwin) {
        // Case 1 — Imported twin. The new canonical wins; the Imported record
        // merges into it on submit.
        nextWarning =
          `"${chosenName}" is already in this reconciliation. You're converting ${subjectRef} into a new ` +
          `${newNoun} entity named "${chosenName}". On submit, ${importedTwin.name} · Imported · ` +
          `#${importedTwin.id} will merge into it. Your new entity survives.`;
        nextCase = 1;
      } else if (
        !typeFixed &&
        establishedType &&
        chosen &&
        chosen !== establishedType
      ) {
        // Editable-but-conflicting type (#24): allowed, but it can't auto-create a
        // surviving entity of the reconciliation's established type — an Individual
        // and an Organization can't reconcile together, so it lands as its own.
        nextWarning =
          `Cannot auto-create a surviving entity — this ${newNoun} conflicts with the ` +
          `reconciliation's current ${establishedNoun} survivor. It will be added as its own entity. ` +
          'Choose "Add as New" again to continue.';
        nextCase = null;
      }

      if (nextWarning && nextWarning !== warning) {
        setWarning(nextWarning);
        setCollisionCase(nextCase);
        return;
      }

      commit(payload);
    },
    [
      typeFixed,
      establishedType,
      establishedNoun,
      entityId,
      name,
      twinsByNameKey,
      warning,
      commit,
      clearErrors,
      setError,
    ]
  );

  const primaryButtonText =
    collisionCase === 1
      ? 'Create and merge'
      : collisionCase === 2
        ? 'Create anyway'
        : 'Add as New';

  return (
    <Modal
      open={open}
      modalHeading="Add as New"
      primaryButtonText={primaryButtonText}
      secondaryButtonText="Cancel"
      primaryButtonDisabled={pending || !formValid}
      onRequestSubmit={() => formRef.current?.submit()}
      onRequestClose={onClose}
      preventCloseOnClickOutside
    >
      {error && (
        <InlineNotification
          kind="error"
          lowContrast
          title=""
          subtitle={error}
          onCloseButtonClick={() => setError(null)}
        />
      )}
      <p>{DESCRIPTION}</p>
      {/* #24 — the type is fixed to the established canonical type because the
          subject is already part of the reconciliation. */}
      {typeFixed && establishedNoun && (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          title={`Entity type fixed to ${establishedNoun}`}
          subtitle={
            'This entity is already in the reconciliation, so it must stay ' +
            `an ${establishedNoun}. Remove it from the reconciliation first ` +
            'to add it as a different type.'
          }
        />
      )}
      {/* #24 — the type is editable, but the reconciliation already resolves to a
          single canonical type; choosing the other type will warn before commit. */}
      {!typeFixed && establishedNoun && (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          title={`This reconciliation's survivor is an ${establishedNoun}`}
          subtitle={
            `Add this entity as an ${establishedNoun} to include it as a ` +
            'surviving entity. Choosing the other type is allowed, but it will ' +
            'be added as its own entity.'
          }
        />
      )}
      {warning && (
        <InlineNotification
          kind="warning"
          lowContrast
          title=""
          subtitle={warning}
          onCloseButtonClick={() => setWarning(null)}
        />
      )}
      {open && (
        <EntityForm
          ref={formRef}
          entity={undefined}
          getEntity={undefined}
          loading={false}
          entityTypeValue={establishedType ?? 'OrganizationEntity'}
          seedValues={seedValues}
          hideSubmitButton
          showCancel={false}
          stacked
          onValidityChange={setFormValid}
          onSubmitPayload={handleSubmit}
          fieldErrors={fieldErrors}
          onFieldErrorsStale={clearFieldErrors}
        />
      )}
    </Modal>
  );
};

export default EntityCanonizePanel;
